import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, CheckCircle, Clock, Shield,
  ChevronDown, ChevronUp, Key, Laptop, Mail, MessageSquare,
  GitBranch, Database, Zap, RotateCcw, Users, FileText, Hash, Eye
} from 'lucide-react';
import { cases, actionBundles, getCaseById, getActionBundleById } from '../data/caseData';

const ConnectorIcon = ({ name }) => {
  const icons = {
    okta: Key,
    entra_id: Key,
    google_workspace: Mail,
    intune: Laptop,
    slack: MessageSquare,
    github: GitBranch,
    figma: Zap,
    salesforce: Database
  };
  const Icon = icons[name] || Zap;
  return <Icon className="w-4 h-4" />;
};

// Draft bundle for new cases
const draftBundle = {
  id: "ab_draft",
  caseId: "draft",
  sequence: 1,
  status: "draft",
  intentSummary: "New action bundle - configure actions below",
  actions: [
    {
      id: "act_draft_001",
      agent: "endpoint_agent",
      connector: "intune",
      connectorIcon: "laptop",
      operation: "policy.apply",
      description: "Apply compliance policy",
      parameters: {
        target: "Selected devices",
        policy: "security-baseline-v2"
      },
      idempotencyKey: `idem_${Date.now()}_policy_apply`,
      riskTier: "medium",
      reversible: true,
      rollbackOperation: "policy.revert",
      status: "draft"
    }
  ],
  blastRadius: {
    usersAffected: 0,
    systemsAffected: 1,
    dataScope: "configurable",
    reversibility: "full",
    estimatedDuration: "5m"
  },
  policyChecks: [
    { policy: "Change Window", result: "pass", message: "Within approved maintenance window" },
    { policy: "Blast Radius Limit", result: "pass", message: "Under 100 device threshold" }
  ],
  verificationGates: [
    { name: "Pre-flight check", description: "Verify target devices are online", status: "pending" },
    { name: "Canary deployment", description: "Deploy to 5% of targets first", status: "pending" },
    { name: "Health check", description: "Confirm no adverse impact after 15m", status: "pending" },
    { name: "Full rollout", description: "Deploy to remaining targets", status: "pending" }
  ],
  approval: {
    required: true,
    reason: "Medium-risk action requires approval",
    approvers: ["IT Operations"],
    approvedBy: null,
    requestedAt: new Date().toISOString()
  },
  execution: {
    startedAt: null,
    completedAt: null,
    mode: "execute"
  },
  createdAt: new Date().toISOString()
};

export const ActionBundleReview = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [showRollback, setShowRollback] = useState(false);
  const [showDiff, setShowDiff] = useState(true);
  const [showVerification, setShowVerification] = useState(true);
  const [comment, setComment] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [approved, setApproved] = useState(false);

  // Handle both existing cases and new/draft cases
  const isNewCase = caseId?.includes('_new') || caseId?.includes('draft');
  const caseData = isNewCase ? null : getCaseById(caseId);
  const bundle = isNewCase ? draftBundle : (caseData ? getActionBundleById(caseData.currentBundle) : null);

  // Add verification gates to existing bundles if not present
  const bundleWithGates = bundle ? {
    ...bundle,
    verificationGates: bundle.verificationGates || [
      { name: "Pre-execution check", description: "Validate preconditions", status: "pending" },
      { name: "Action execution", description: "Execute all actions in sequence", status: "pending" },
      { name: "Post-execution verification", description: "Confirm expected state", status: "pending" },
      { name: "Evidence collection", description: "Package audit evidence", status: "pending" }
    ],
    actions: bundle.actions.map((action, idx) => ({
      ...action,
      idempotencyKey: action.idempotencyKey || `idem_${bundle.id}_${action.id}_${idx}`
    }))
  } : null;

  if (!bundleWithGates) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">Bundle not found</h2>
          <button
            onClick={() => navigate('/control-tower')}
            className="mt-4 px-4 py-2 bg-[#4a70ff] text-white text-sm rounded-lg"
          >
            Back to Control Tower
          </button>
        </div>
      </div>
    );
  }

  const handleApprove = () => {
    setIsApproving(true);
    setTimeout(() => {
      setIsApproving(false);
      setApproved(true);
      setTimeout(() => {
        navigate(isNewCase ? '/control-tower' : `/case/${caseId}`);
      }, 1500);
    }, 1500);
  };

  const riskColor = {
    low: 'bg-primary/10 text-primary border-primary/20',
    medium: 'bg-slate-200 text-slate-700 border-slate-300',
    high: 'bg-slate-900 text-white border-slate-900'
  };

  const highestRisk = bundleWithGates.actions.reduce((max, action) => {
    const tiers = { low: 0, medium: 1, high: 2 };
    return tiers[action.riskTier] > tiers[max] ? action.riskTier : max;
  }, 'low');

  if (approved) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Bundle Approved</h2>
          <p className="text-sm text-gray-500 mt-1">Execution will begin shortly...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="action-bundle-review-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
          <button
            onClick={() => navigate(isNewCase ? '/control-tower' : `/case/${caseId}`)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-3"
            data-testid="back-navigation-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {isNewCase ? 'Back to Control Tower' : 'Back to Case'}
          </button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-semibold text-black">Action Bundle Review</h1>
              <p className="text-sm text-gray-500 mt-1">{bundleWithGates.intentSummary}</p>
            </div>
            <span className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${riskColor[highestRisk]}`}>
              {highestRisk.toUpperCase()} RISK
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-black mb-4">Summary</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Case</span>
                  <p className="font-medium text-gray-900">
                    {isNewCase ? 'New Bundle' : `${caseData?.typeLabel}: ${caseData?.subject?.name}`}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Requested By</span>
                  <p className="font-medium text-gray-900">
                    {isNewCase ? 'Manual' : caseData?.cluster?.agents?.[0] || 'System'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Actions</span>
                  <p className="font-medium text-gray-900">{bundleWithGates.actions.length} actions</p>
                </div>
                <div>
                  <span className="text-gray-400">Requested At</span>
                  <p className="font-medium text-gray-900">
                    {bundleWithGates.approval?.requestedAt
                      ? new Date(bundleWithGates.approval.requestedAt).toLocaleString()
                      : 'Now'}
                  </p>
                </div>
              </div>
            </div>

            {/* Blast Radius */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="blast-radius-card">
              <h2 className="text-sm font-semibold text-black mb-4">Blast Radius</h2>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <Users className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-lg font-semibold text-gray-900">{bundleWithGates.blastRadius.usersAffected}</p>
                  <span className="text-xs text-gray-400">Users</span>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <Zap className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-lg font-semibold text-gray-900">{bundleWithGates.blastRadius.systemsAffected}</p>
                  <span className="text-xs text-gray-400">Systems</span>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-gray-900 capitalize">{bundleWithGates.blastRadius.dataScope.replace(/_/g, ' ')}</p>
                  <span className="text-xs text-gray-400">Scope</span>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <RotateCcw className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-gray-900 capitalize">{bundleWithGates.blastRadius.reversibility}</p>
                  <span className="text-xs text-gray-400">Reversible</span>
                </div>
              </div>
            </div>

            {/* Actions with Idempotency Keys */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowDiff(!showDiff)}
              >
                <h2 className="text-sm font-semibold text-black">Actions ({bundleWithGates.actions.length})</h2>
                {showDiff ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>

              {showDiff && (
                <div className="mt-4 space-y-3">
                  {bundleWithGates.actions.map((action, idx) => (
                    <div key={action.id} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded bg-gray-50 flex items-center justify-center">
                              <ConnectorIcon name={action.connector} />
                            </div>
                            <span className="text-sm font-medium text-gray-900">{action.description}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${action.riskTier === 'low' ? 'bg-primary/10 text-primary' :
                                action.riskTier === 'medium' ? 'bg-slate-200 text-slate-700' :
                                  'bg-slate-900 text-white'
                              }`}>
                              {action.riskTier}
                            </span>
                            {!action.reversible && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-900 text-white">
                                irreversible
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {action.connector} / {action.operation}
                          </div>

                          {/* Idempotency Key */}
                          <div className="mt-2 flex items-center gap-2 text-xs">
                            <Hash className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-500">Idempotency:</span>
                            <code className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">
                              {action.idempotencyKey}
                            </code>
                          </div>

                          {/* Parameters */}
                          <div className="mt-3 bg-gray-50 rounded-lg p-3 font-mono text-xs">
                            <pre className="text-gray-600 overflow-x-auto">
                              {JSON.stringify(action.parameters, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Verification Gates */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowVerification(!showVerification)}
              >
                <h2 className="text-sm font-semibold text-black flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#4a70ff]" />
                  Verification Gates
                </h2>
                {showVerification ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>

              {showVerification && (
                <div className="mt-4 space-y-2">
                  {bundleWithGates.verificationGates.map((gate, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-500">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{gate.name}</div>
                        <div className="text-xs text-gray-500">{gate.description}</div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${gate.status === 'pass' ? 'bg-primary/10 text-primary' :
                          gate.status === 'fail' ? 'bg-slate-900 text-white' :
                            'bg-gray-100 text-gray-500'
                        }`}>
                        {gate.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Rollback Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowRollback(!showRollback)}
              >
                <h2 className="text-sm font-semibold text-black flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-gray-400" />
                  Rollback Plan
                </h2>
                {showRollback ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>

              {showRollback && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500 mb-3">If rollback is triggered, the following actions will be executed in reverse order:</p>
                  <div className="space-y-2">
                    {bundleWithGates.actions.filter(a => a.reversible).map((action, idx) => (
                      <div key={action.id} className="flex items-center gap-2 text-sm">
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                          {idx + 1}
                        </span>
                        <span className="text-gray-600">
                          {action.connector}.{action.rollbackOperation || 'reverse_' + action.operation}
                        </span>
                      </div>
                    ))}
                    {bundleWithGates.actions.some(a => !a.reversible) && (
                      <div className="mt-3 p-3 bg-slate-100 rounded-lg text-sm text-slate-700">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        Some actions are irreversible and cannot be rolled back.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Approval */}
          <div className="space-y-6">
            {/* Policy Checks */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="policy-checks-list">
              <h2 className="text-sm font-semibold text-black mb-4 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#4a70ff]" />
                Policy Checks
              </h2>
              <div className="space-y-3">
                {bundleWithGates.policyChecks.map((check, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    {check.result === 'pass' ? (
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    ) : check.result === 'pending' ? (
                      <Clock className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-slate-900 mt-0.5 shrink-0" />
                    )}
                    <div>
                      <span className="text-sm text-gray-900">{check.policy}</span>
                      <p className="text-xs text-gray-400">{check.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Status */}
            {bundleWithGates.approval && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-semibold text-black mb-4">Approval Status</h2>
                <div className="space-y-3">
                  <div className="text-sm">
                    <span className="text-gray-400">Required Approvers</span>
                    <div className="mt-1 space-y-1">
                      {bundleWithGates.approval.approvers?.map((approver, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Clock className="w-3 h-3 text-slate-500" />
                          <span className="text-gray-700">{approver}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {bundleWithGates.approval.approvedBy && bundleWithGates.approval.approvedBy.length > 0 && (
                    <div className="text-sm">
                      <span className="text-gray-400">Already Approved</span>
                      <div className="mt-1 space-y-1">
                        {bundleWithGates.approval.approvedBy.map((approval, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-primary" />
                            <span className="text-gray-700">{approval.name}</span>
                            <span className="text-xs text-gray-400">
                              {new Date(approval.at).toLocaleTimeString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Decision */}
            <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="approval-decision-buttons">
              <h2 className="text-sm font-semibold text-black mb-4">Decision</h2>

              <div className="mb-4">
                <label className="text-sm text-gray-500 block mb-1.5">Comment (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note about your decision..."
                  className="w-full h-20 px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#4a70ff]/20 focus:border-[#4a70ff]"
                />
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="w-full px-4 py-2.5 bg-[#4a70ff] text-white text-sm font-medium rounded-lg hover:bg-[#3a5fd9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isApproving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Approve and Execute
                    </>
                  )}
                </button>
                <button
                  className="w-full px-4 py-2.5 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Block
                </button>
                <button
                  className="w-full px-4 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Request More Evidence
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionBundleReview;
